import React, { useState } from "react";
import { Drawer } from "@mui/material";
import CustomTooltip from "@product-app/common/components/CustomTooltip";
import { WorkflowCenterBar } from "@product-app/common/workflows/components/WorkflowCenterBar";
import { useAuth } from "../auth/useAuth";
import { oraLogoUrl } from "../assetUrl";
import FlowPasswordManager from "./FlowPasswordManager";
import {
  APPSHELL_ASSISTANT_HEADER_TESTID,
  APPSHELL_COMPOSE_TESTID,
  APPSHELL_CONTENT_TESTID,
  APPSHELL_SWITCH_LABEL,
} from "./appShellIds";

/**
 * `needs_you` is the QUEUE-FIRST tab (story pinned-pane, AC-2): the pane opens
 * on it and stays there. Launch is the catalog. The old Active tab (In flight
 * / All workflows) is retired — a running hop opens the live drawer.
 */
export type AppShellTab = "needs_you" | "launch" | "active" | "assistant";

export interface AppShellProps {
  children: React.ReactNode;
  demoMode?: boolean;
  onToggleDemo?: () => void;
  currentTab?: AppShellTab;
  onTabChange?: (tab: AppShellTab) => void;
  activeCount?: number;
  /**
   * Badge on the queue-first tab. This is the SERVER's `count` from
   * `list_waiting_for_me`, passed straight through — never a length
   * accumulated from push frames (design Constraint 19).
   */
  needsYouCount?: number;
  onSignOut?: () => void;
  /**
   * The Assistant is on (outlook-orachat-uplift s5): the header is the shared
   * WorkflowCenterBar ("ora Work Simplified") with a Needs you | Assistant
   * underline strip in its own row under it. Off (the default) keeps today's
   * headers exactly. The setting only reads true through an authenticated API
   * call, so it implies sign-in.
   */
  assistantEnabled?: boolean;
  /**
   * A compose window with the Assistant on (outlook-orachat-uplift s6): the
   * same WorkflowCenterBar, no tab strip, and the content area is the chat
   * alone (`APPSHELL_COMPOSE_TESTID`). Ignored with the Assistant off, which
   * keeps today's shell (C12).
   */
  composeWindow?: boolean;
}

export const AppShell: React.FC<AppShellProps> = ({
  children,
  demoMode,
  onToggleDemo,
  currentTab,
  onTabChange,
  activeCount,
  needsYouCount,
  onSignOut,
  assistantEnabled = false,
  composeWindow = false,
}) => {
  const { isAuthenticated, signOut } = useAuth();

  const handleSignOut = () => {
    onSignOut?.();
    void signOut();
  };

  // ── Flow password panel state ──────────────────────────────────────────────
  const [flowPasswordsOpen, setFlowPasswordsOpen] = useState(false);

  // ONE tree for every header: `{children}` keeps its place when the Assistant
  // setting arrives or flips, so the pane's content never remounts under it.
  const compose = assistantEnabled && composeWindow;
  let topBar: React.ReactNode = null;
  if (assistantEnabled) {
    const tab = currentTab === "assistant" ? "assistant" : "needs_you";
    const changeTab = onTabChange ?? (() => undefined);
    topBar = (
      <>
        {/* His bar (Joachim, 2026-10-21): centred "ora Work Simplified".
            Sign out stays on the right; the bar itself has no slot for it.
            The strip under it is the same underline tabs as his pane (C14). */}
        <div className="relative flex-shrink-0" data-testid={APPSHELL_ASSISTANT_HEADER_TESTID}>
          <WorkflowCenterBar logoSrc={oraLogoUrl()} />
          <button
            type="button"
            onClick={handleSignOut}
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded border-none bg-transparent px-2 py-1 text-xs tracking-[0.02em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
          >
            Sign out
          </button>
        </div>
        {/* A compose window has only the chat, so no strip (s6). */}
        {!compose && (
          <div
            role="tablist"
            aria-label={APPSHELL_SWITCH_LABEL}
            className="flex flex-shrink-0 border-b border-primary-gray-light-200 bg-base-white"
          >
            <TabButton
              tab="needs_you"
              label="Needs you"
              currentTab={tab}
              onTabChange={changeTab}
              badge={needsYouCount}
            />
            <TabButton
              tab="assistant"
              label="Assistant"
              currentTab={tab}
              onTabChange={changeTab}
            />
          </div>
        )}
      </>
    );
  } else if (isAuthenticated) {
    topBar = (
      <div
        className="flex flex-shrink-0 items-center gap-2 border-b border-primary-gray-light-200 bg-base-white px-4 py-3"
        data-testid="appshell-today-header"
      >
        <span className="h-[9px] w-[9px] rounded-full border border-black bg-sunrise" />
        <span
          className="text-[15px] font-medium text-primary-gray-light-900"
          data-testid="appshell-title"
        >
          ora · Today
        </span>
        <span className="ml-auto text-[11px] text-primary-gray-light-400">
          pinned
        </span>
        <button
          type="button"
          onClick={handleSignOut}
          className="rounded border-none bg-transparent px-2 py-1 text-xs tracking-[0.02em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-primary-gray-light-50 font-sans">
      {topBar}
      {/* ── Brand Hero (unauthenticated / login) ─────────────────── */}
      {!topBar && (
      <div
        className="relative flex-shrink-0 text-primary-gray-light-700"
        data-testid="appshell-hero"
      >
        {isAuthenticated && (
          <>
            {onToggleDemo && (
              <div className="absolute left-3 top-3">
                {/* ora's tooltip, not a native title (story 28633). Node
                    title + describeChild: "Demo" stays the button's name. */}
                <CustomTooltip
                  id="appshell-demo-toggle"
                  title={
                    <span>
                      {demoMode
                        ? "Demo mode on — click to turn off"
                        : "Turn on demo mode"}
                    </span>
                  }
                  describeChild
                  placement="bottom-start"
                  arrow
                  isOnModal
                >
                  <button
                    type="button"
                    onClick={onToggleDemo}
                    className={
                      "rounded-[10px] px-2 py-[2px] text-2xs font-semibold tracking-[0.01em] transition-colors " +
                      (demoMode
                        ? "border border-primary-warning-300 bg-primary-warning-50 text-primary-warning-700"
                        : "border border-primary-gray-light-200 bg-transparent text-primary-gray-light-400 hover:text-primary-gray-light-600")
                    }
                  >
                    {demoMode ? "● Demo" : "Demo"}
                  </button>
                </CustomTooltip>
              </div>
            )}
            <div className="absolute right-3 top-3 flex flex-row items-center gap-1">
              <button
                type="button"
                onClick={() => setFlowPasswordsOpen(true)}
                className="rounded border-none bg-transparent px-2 py-1 text-xs tracking-[0.02em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
                aria-label="Manage flow passwords"
                data-testid="appshell-flow-passwords-btn"
              >
                Flow passwords
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="rounded border-none bg-transparent px-2 py-1 text-xs tracking-[0.02em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
              >
                Sign out
              </button>
            </div>

            {/* Flow passwords drawer — standing self-serve panel */}
            <Drawer
              anchor="bottom"
              open={flowPasswordsOpen}
              onClose={() => setFlowPasswordsOpen(false)}
              PaperProps={{
                sx: {
                  maxHeight: "80vh",
                  borderRadius: "12px 12px 0 0",
                  overflowY: "auto",
                },
              }}
            >
              <FlowPasswordManager onClose={() => setFlowPasswordsOpen(false)} />
            </Drawer>
          </>
        )}

        {/* The "oraWorkflows" brand bar — the SAME component every takeover
            wears (story 28633), so the logo never changes between screens. */}
        <WorkflowCenterBar logoSrc={oraLogoUrl()} testId="appshell-compact-label" />
      </div>
      )}

      {/* ── Tab bar ─────────────────────────────────────────────── */}
      {!topBar && isAuthenticated && currentTab && onTabChange && (
        <div
          className="flex flex-shrink-0 border-b border-primary-gray-light-200 bg-base-white"
          role="tablist"
        >
          {/* FIRST in the strip and the default on open — the AM's day starts
              with what is waiting on them, not with a launch form. */}
          <TabButton
            tab="needs_you"
            label="Needs you"
            currentTab={currentTab}
            onTabChange={onTabChange}
            badge={needsYouCount}
          />
          <TabButton
            tab="launch"
            label="Launch"
            currentTab={currentTab}
            onTabChange={onTabChange}
          />
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────── */}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        data-testid={compose ? APPSHELL_COMPOSE_TESTID : APPSHELL_CONTENT_TESTID}
      >
        {children}
      </div>
    </div>
  );
};

interface TabButtonProps {
  tab: AppShellTab;
  label: string;
  currentTab: AppShellTab;
  onTabChange: (tab: AppShellTab) => void;
  badge?: number;
}

const TabButton: React.FC<TabButtonProps> = ({
  tab,
  label,
  currentTab,
  onTabChange,
  badge,
}) => {
  const active = currentTab === tab;
  // Same strip as his pane (PaneFrame): full width, uppercase, yellow underline.
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onTabChange(tab)}
      className={
        "relative flex h-10 flex-1 items-center justify-center px-4 text-xs font-semibold uppercase tracking-[0.05em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sunrise " +
        (active
          ? "text-primary-gray-light-900"
          : "text-primary-gray-light-500 hover:text-primary-gray-light-700")
      }
    >
      <span>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="ml-1.5 rounded-full bg-sunrise px-2 py-[1px] text-2xs font-bold text-primary-gray-light-900">
          {badge}
        </span>
      )}
      <span
        aria-hidden="true"
        className={
          "absolute inset-x-0 bottom-0 h-[2px] transition-transform duration-200 ease-out " +
          (active ? "scale-x-100 bg-sunrise" : "scale-x-0 bg-transparent")
        }
      />
    </button>
  );
};

export default AppShell;
